import type { CaptureResult } from "posthog-js";
import {
  createMirrorSpeechMetrics, MIRROR_SPEECH_TIMING_KEYS, validateMirrorSpeechTimingReport,
  type MirrorSpeechMetadata, type MirrorSpeechMetrics, type MirrorSpeechTimingReport,
  type MirrorSpeechTerminal, type MirrorSpeechTerminalReason,
} from "@/lib/mirrorSpeechMetrics";

export const MIRROR_SPEECH_EVENT = "mirror_speech_latency";

/** This runs after SDK enrichment. Rebuild the entire scoped event, including
 * its identity; never forward SDK super properties, URLs, $set or session IDs. */
export function sanitizeMirrorSpeechTelemetry(
  event: CaptureResult | null, publicToken: string | undefined,
): CaptureResult | null {
  if (!event || event.event !== MIRROR_SPEECH_EVENT) return event;
  try {
    if (!publicToken || !event.properties) return null;
    const metrics: Record<string, unknown> = {};
    for (const key of MIRROR_SPEECH_TIMING_KEYS) metrics[key] = event.properties[key];
    const report = validateMirrorSpeechTimingReport(metrics);
    if (!report) return null;
    const distinctId = crypto.randomUUID(), eventUuid = crypto.randomUUID();
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(distinctId)
      || !/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(eventUuid)) return null;
    return {
      event: MIRROR_SPEECH_EVENT, uuid: eventUuid, timestamp: new Date(),
      properties: {
        ...report, token: publicToken, distinct_id: distinctId,
        $process_person_profile: false, $ip: "0.0.0.0", $geoip_disable: true,
      },
    };
  } catch { return null; }
}

export interface MirrorSpeechMeasurement extends MirrorSpeechMetrics {
  /** Ephemeral DOM correlation only, never included in a report. */
  readonly marker: number;
  readonly voiceEnabled: boolean | null;
  /** Local prepared-speech permission, independent of telemetry delivery. */
  canSpeak(): boolean;
}
export interface MirrorSpeechCoordinator {
  configure(metadata: MirrorSpeechMetadata): void;
  begin(options: {
    isCurrent: () => boolean; visibleTextExpected: boolean;
    mode: "normal" | "turn_voice" | "manual_listen";
    trigger: "autoplay" | "manual"; voiceEnabled: boolean | null;
  }): MirrorSpeechMeasurement;
  committedText(marker: number): void;
  finishCurrent(terminal: MirrorSpeechTerminal, reason: MirrorSpeechTerminalReason): void;
  discard(): void;
}

/** Installed unconditionally by MirrorChat. Generic transport callbacks may
 * omit this telemetry integration, just as they may omit an analytics sink. */
export function createMirrorSpeechCoordinator(options: {
  onResult: (report: MirrorSpeechTimingReport) => void;
  now?: () => number;
}): MirrorSpeechCoordinator {
  let metadata: MirrorSpeechMetadata = {}, current: MirrorSpeechMeasurement | null = null, sequence = 0;
  let revokeSpeech: (() => void) | null = null;
  return {
    configure(value) { metadata = { ...value }; },
    begin(value) {
      revokeSpeech?.(); current?.discard();
      const marker = ++sequence;
      let speechPermitted = true;
      revokeSpeech = () => { speechPermitted = false; };
      const metrics = createMirrorSpeechMetrics({
        metadata: { ...metadata, mode: value.mode, trigger: value.trigger, voice_enabled: value.voiceEnabled },
        visibleTextExpected: value.visibleTextExpected,
        isCurrent: () => current?.marker === marker && value.isCurrent(),
        onResult: options.onResult, now: options.now,
      });
      current = { ...metrics, marker, voiceEnabled: value.voiceEnabled,
        canSpeak: () => speechPermitted && current?.marker === marker };
      current.acceptedSend();
      return current;
    },
    committedText(marker) { if (current?.marker === marker) current.firstVisibleText(); },
    finishCurrent(terminal, reason) {
      if (terminal === "stop") revokeSpeech?.();
      current?.finish(terminal, reason);
    },
    discard() { revokeSpeech?.(); current?.discard(); current = null; revokeSpeech = null; },
  };
}

/** Network type is explicit browser metadata; effectiveType is not Wi-Fi proof. */
export function mirrorSpeechNetwork(): MirrorSpeechTimingReport["network"] {
  try {
    if (navigator.onLine === false) return "offline";
    const connection = (navigator as Navigator & { connection?: { type?: string } }).connection;
    const type = connection?.type;
    return type === "wifi" || type === "cellular" || type === "ethernet" ? type : "unknown";
  } catch { return "unknown"; }
}
